<template>
  <div class="aco-page py-3">
    <header class="aco-toolbar">
      <div class="aco-toolbar__title">
        <span class="aco-toolbar__accent" aria-hidden="true"></span>
        <div>
          <h1>Amazon Care-of Verification</h1>
          <p>Match Amazon orders (name + address only) to OMS contacts — credit telesales staff and fill the real phone</p>
        </div>
      </div>
      <div class="aco-toolbar__actions">
        <CButton size="sm" class="aco-apply-all-btn" :disabled="isApplyingAll || !summary.confident" @click="askApplyAll">
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <polyline points="20 6 9 17 4 12" />
          </svg>
          <span>{{ isApplyingAll ? 'Applying…' : `Apply All Confident (${summary.confident || 0})` }}</span>
        </CButton>
        <CButton size="sm" class="aco-recompute-btn" :disabled="isRecomputing" @click="recompute">
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" :class="{ 'is-spinning': isRecomputing }" aria-hidden="true">
            <polyline points="23 4 23 10 17 10" />
            <polyline points="1 20 1 14 7 14" />
            <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
          </svg>
          <span>{{ isRecomputing ? 'Recomputing…' : 'Recompute' }}</span>
        </CButton>
      </div>
    </header>

    <CAlert v-if="errorMessage" color="danger" show class="aco-alert">{{ errorMessage }}</CAlert>
    <CAlert v-if="successMessage" color="success" show class="aco-alert">{{ successMessage }}</CAlert>

    <section class="aco-tiles">
      <article class="aco-tile aco-tile--total">
        <div class="aco-tile__label">Pending Orders</div>
        <div class="aco-tile__value">{{ summary.total }}</div>
        <div class="aco-tile__breakdown"><span>Amazon orders awaiting care-of</span></div>
      </article>
      <article class="aco-tile aco-tile--confident">
        <div class="aco-tile__label">Confident</div>
        <div class="aco-tile__value">{{ summary.confident }}</div>
        <div class="aco-tile__breakdown"><span>Single contact + telesales call — one-click apply</span></div>
      </article>
      <article class="aco-tile aco-tile--review">
        <div class="aco-tile__label">Needs Review</div>
        <div class="aco-tile__value">{{ summary.needs_review }}</div>
        <div class="aco-tile__breakdown"><span>Multiple matches, no call, or weak (name-only)</span></div>
      </article>
      <article class="aco-tile aco-tile--nomatch">
        <div class="aco-tile__label">No Match</div>
        <div class="aco-tile__value">{{ summary.no_match }}</div>
        <div class="aco-tile__breakdown"><span>No OMS contact shares this name</span></div>
      </article>
    </section>

    <section class="aco-section">
      <header class="aco-section__head"><h2>Filters</h2></header>
      <div class="aco-section__body">
        <div class="aco-filters">
          <div class="aco-field aco-field--full">
            <label class="aco-label">Search</label>
            <AppInput no-margin :value="filters.search" @update:value="onSearchInput" placeholder="Search by order number, customer name, or address…" />
          </div>
          <div class="aco-field">
            <label class="aco-label">From Order Date</label>
            <SingleDatePicker :value="filters.from" @input="filters.from = $event" />
          </div>
          <div class="aco-field">
            <label class="aco-label">To Order Date</label>
            <SingleDatePicker :value="filters.to" @input="filters.to = $event" />
          </div>
          <div class="aco-field">
            <label class="aco-label">Organisation</label>
            <AppDropdown v-model="filters.organisation_id" :options="orgOptions" placeholder="Any Organisation" />
          </div>
          <div class="aco-field">
            <label class="aco-label">Status</label>
            <AppDropdown v-model="filters.status" :options="statusOptions" placeholder="Any Status" />
          </div>
          <div class="aco-field aco-field--action">
            <label class="aco-label" aria-hidden="true">&nbsp;</label>
            <button type="button" class="aco-apply-btn" :disabled="isLoading" @click="applyFilters">{{ isLoading ? 'Loading…' : 'Apply' }}</button>
          </div>
          <div class="aco-field aco-field--action">
            <label class="aco-label" aria-hidden="true">&nbsp;</label>
            <button type="button" class="aco-reset-btn" @click="resetFilters">Reset</button>
          </div>
        </div>
      </div>
    </section>

    <BaseTable
      title="Amazon Orders"
      :items="tableRows"
      :columns="columns"
      :loading="isLoading"
      :sortable="false"
      :show-count="false"
      max-body-height="65vh"
      empty-text="No pending Amazon orders match these filters."
    >
      <template #toolbar>
        <span v-if="pagination.total" class="aco-count">{{ rangeLabel }}</span>
      </template>
      <template #cell-customer="{ row }">
        <div class="aco-customer-cell">
          <span class="aco-customer-name">{{ row.customer }}</span>
          <span class="aco-customer-addr" :title="row.address">{{ row.address || '—' }}</span>
        </div>
      </template>
      <template #cell-matched="{ row }">
        <div v-if="row._real_phone" class="aco-match-cell">
          <span class="aco-match-phone">{{ row._real_phone }}</span>
          <span v-if="row._matched_contact_name" class="aco-match-name">{{ row._matched_contact_name }}</span>
        </div>
        <span v-else class="aco-muted">—</span>
      </template>
      <template #cell-staff="{ row }">
        <span v-if="row._care_of_name" class="aco-staff">{{ row._care_of_name }}</span>
        <span v-else class="aco-muted">—</span>
      </template>
      <template #cell-status="{ row }">
        <span class="aco-pill" :class="statusPillClass(row._status)">{{ statusLabel(row._status) }}</span>
        <div v-if="row._reason" class="aco-reason">{{ row._reason }}</div>
      </template>
      <template #cell-action="{ row }">
        <div class="aco-row-actions">
          <template v-if="row._status === 'confident'">
            <button type="button" class="aco-act-btn" :disabled="applyingIds[row._id]" @click="applyConfident(row)">
              {{ applyingIds[row._id] ? '…' : 'Apply' }}
            </button>
            <button type="button" class="aco-act-btn aco-act-btn--ghost" @click="openReview(row)">Review</button>
          </template>
          <button
            v-else-if="row._status === 'needs_review'"
            type="button"
            class="aco-act-btn aco-act-btn--review"
            @click="openReview(row)"
          >
            Review
          </button>
          <button
            v-else-if="row._status === 'credited'"
            type="button"
            class="aco-act-btn aco-act-btn--danger"
            @click="askUncredit(row)"
          >
            Uncredit
          </button>
          <button
            v-else-if="row._status === 'rejected' || row._status === 'oms_customer'"
            type="button"
            class="aco-act-btn aco-act-btn--ghost"
            @click="askReopen(row)"
          >
            Reopen
          </button>
          <span v-else class="aco-muted">—</span>
        </div>
      </template>
    </BaseTable>

    <div v-if="!isLoading && tableRows.length && pagination.last_page > 1" class="aco-pagination-bar">
      <span class="aco-pagination-info">{{ rangeLabel }}</span>
      <AppPager :current-page="Number(pagination.current_page) || 1" :last-page="Number(pagination.last_page) || 1" @change="goToPage" />
    </div>

    <CModal
      :show.sync="reviewOpen"
      size="lg"
      :centered="true"
      title="Review Amazon Order"
      class="aco-review-modal"
      :close-on-backdrop="!isApprovingReview"
    >
      <div v-if="reviewRow" class="aco-review-body">
        <div v-if="reviewError" class="aco-review-alert">{{ reviewError }}</div>

        <div class="aco-review-order">
          <div class="aco-review-order__head">
            <span class="aco-review-eyebrow">Amazon Order</span>
            <h3>{{ reviewRow.customer || '—' }}</h3>
          </div>
          <div class="aco-review-order__grid">
            <div><span class="aco-rk">Order #</span><span class="aco-rv">{{ reviewRow.order_number || '—' }}</span></div>
            <div><span class="aco-rk">Placeholder Phone</span><span class="aco-rv">{{ reviewRow.phone || '—' }}</span></div>
            <div class="aco-review-order__addr"><span class="aco-rk">Address</span><span class="aco-rv">{{ reviewRow.address || '—' }}</span></div>
            <div v-if="reviewOrderTokens.length" class="aco-review-order__addr">
              <span class="aco-rk">Address tokens</span>
              <span class="aco-rv"><span v-for="t in reviewOrderTokens" :key="t" class="aco-token">{{ t }}</span></span>
            </div>
          </div>
        </div>

        <div class="aco-review-head">
          <h4>Matching OMS Contacts <span v-if="!isLoadingCandidates">({{ reviewCandidates.length }})</span></h4>
          <p>Contacts that share this customer's name and overlap the address. Pick the one this order belongs to — its phone fills the order and its telesales call sets care-of.</p>
        </div>

        <div v-if="isLoadingCandidates" class="aco-review-empty">Loading candidates…</div>
        <div v-else-if="!reviewCandidates.length" class="aco-review-empty">No name + address match for this order. Assign an agent manually below if a telesales staffer claims it (the phone won't change).</div>
        <div v-else class="aco-review-cards">
          <label
            v-for="cand in reviewCandidates"
            :key="cand.contact_id"
            class="aco-cand"
            :class="{ 'is-picked': pickedContactId === cand.contact_id }"
          >
            <input type="radio" :value="cand.contact_id" :checked="pickedContactId === cand.contact_id" class="aco-cand__radio" @change="pickCandidate(cand)" />
            <div class="aco-cand__body">
              <div class="aco-cand__head">
                <span class="aco-cand__name">{{ cand.contact_name || '—' }}</span>
                <span class="aco-cand__phone">{{ cand.phone }}</span>
              </div>
              <div v-if="cand.addresses && cand.addresses.length" class="aco-cand__row">
                <span class="aco-rk">Address</span>
                <span class="aco-rv"><span v-for="(a, i) in cand.addresses" :key="i" class="aco-cand__addr">{{ a }}</span></span>
              </div>
              <div v-if="cand.matched_tokens && cand.matched_tokens.length" class="aco-cand__row">
                <span class="aco-rk">Matched tokens</span>
                <span class="aco-rv"><span v-for="t in cand.matched_tokens" :key="t" class="aco-token aco-token--match">{{ t }}</span></span>
              </div>
              <div class="aco-cand__row">
                <span class="aco-rk">Care-of</span>
                <span class="aco-rv">
                  <span v-if="cand.care_of_user_name" class="aco-staff">{{ cand.care_of_user_name }}</span>
                  <span v-else class="aco-muted">No telesales call in this org — pick an agent below</span>
                </span>
              </div>
            </div>
          </label>
        </div>

        <div class="aco-review-head">
          <h4>Assign Agent Manually <span class="aco-optional">(optional override)</span></h4>
          <p>Override the care-of agent, or credit one when no call matched. The phone still comes from the picked contact above.</p>
        </div>
        <div class="aco-manual-assign">
          <AppDropdown v-model="manualAgentId" :options="manualAgentOptions" searchable search-placeholder="Search agent…" placeholder="Use matched agent" />
        </div>
      </div>
      <template #footer>
        <div class="aco-modal-foot">
          <div class="aco-modal-foot__left">
            <button type="button" class="aco-modal-btn aco-modal-btn--danger" :disabled="isApprovingReview" @click="dismissFromReview('rejected')">Reject</button>
            <button type="button" class="aco-modal-btn aco-modal-btn--warn" :disabled="isApprovingReview" @click="dismissFromReview('oms_customer')">OMS Customer</button>
          </div>
          <div class="aco-modal-foot__right">
            <button type="button" class="aco-modal-btn aco-modal-btn--ghost" :disabled="isApprovingReview" @click="closeReview">Cancel</button>
            <button type="button" class="aco-modal-btn aco-modal-btn--primary" :disabled="isApprovingReview || !canApproveReview" @click="approveReview">
              {{ isApprovingReview ? 'Applying…' : approveLabel }}
            </button>
          </div>
        </div>
      </template>
    </CModal>

    <AppConfirmDialog
      :visible="confirmApplyAllOpen"
      title="Apply All Confident Matches?"
      :message="applyAllMessage"
      confirm-label="Apply All"
      busy-label="Applying…"
      cancel-label="Cancel"
      :busy="isApplyingAll"
      variant="warning"
      @confirm="confirmApplyAll"
      @cancel="cancelApplyAll"
      @update:visible="(v) => !v && cancelApplyAll()"
    />

    <AppConfirmDialog
      :visible="rowActionOpen"
      :title="rowAction ? rowAction.title : ''"
      :message="rowAction ? rowAction.message : ''"
      :confirm-label="rowAction ? rowAction.confirmLabel : 'Confirm'"
      busy-label="Working…"
      cancel-label="Cancel"
      :busy="rowActionBusy"
      :variant="rowAction ? rowAction.variant : 'warning'"
      @confirm="confirmRowAction"
      @cancel="cancelRowAction"
      @update:visible="(v) => !v && cancelRowAction()"
    />
  </div>
</template>

<script>
import axios from 'axios'
import AppInput from '@/components/AppInput.vue'
import AppDropdown from '@/components/AppDropdown.vue'
import SingleDatePicker from '@/components/SingleDatePicker.vue'
import BaseTable from '@/components/BaseTable.vue'
import AppPager from '@/components/AppPager.vue'
import AppConfirmDialog from '@/components/AppConfirmDialog.vue'
import { ORGANISATION_ID_TO_NAME } from '@/views/users/userHelpers.js'

function todayIso () {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

const DEFAULT_SUMMARY = { total: 0, confident: 0, needs_review: 0, no_match: 0, credited: 0, rejected: 0, oms_customer: 0 }

export default {
  name: 'AmazonCareOf',
  components: { AppInput, AppDropdown, SingleDatePicker, BaseTable, AppPager, AppConfirmDialog },
  data () {
    return {
      filters: { from: '', to: todayIso(), organisation_id: '', status: '', search: '' },
      searchDebounce: null,
      agents: [],
      rows: [],
      summary: { ...DEFAULT_SUMMARY },
      pagination: { current_page: 1, per_page: 25, total: 0, last_page: 1 },
      isLoading: false,
      isRecomputing: false,
      isApplyingAll: false,
      applyingIds: {},
      confirmApplyAllOpen: false,
      errorMessage: '',
      successMessage: '',
      reviewOpen: false,
      reviewRow: null,
      reviewOrderTokens: [],
      reviewCandidates: [],
      reviewError: '',
      isLoadingCandidates: false,
      isApprovingReview: false,
      pickedContactId: null,
      pickedCareOfId: null,
      manualAgentId: '',
      rowActionOpen: false,
      rowAction: null,
      rowActionBusy: false
    }
  },
  computed: {
    columns () {
      return [
        { key: 'sno',          label: 'SNo',          width: '64px' },
        { key: 'order_number', label: 'Order #',      width: '130px', sticky: true },
        { key: 'customer',     label: 'Customer',     width: '240px', sticky: true },
        { key: 'phone',        label: 'Placeholder',  width: '120px' },
        { key: 'matched',      label: 'Matched Contact', width: '180px' },
        { key: 'staff',        label: 'Suggested Care-of', width: '150px' },
        { key: 'status',       label: 'Status' },
        { key: 'action',       label: 'Action',       width: '160px', align: 'center', stickyRight: true }
      ]
    },
    tableRows () {
      const page = Number(this.pagination.current_page) || 1
      const perPage = Number(this.pagination.per_page) || 25
      return this.rows.map((row, index) => {
        const o = row.order || {}
        return {
          sno: (page - 1) * perPage + index + 1,
          order_number: o.order_number || ('#' + row.order_id),
          customer: o.contact_name || '—',
          address: this.composeAddress(o),
          phone: o.contact_phone || '—',
          _id: row.order_id,
          _status: row.status,
          _reason: row.reason,
          _real_phone: row.real_phone,
          _matched_contact_id: row.matched_contact_id,
          _matched_contact_name: row.matched_contact_name,
          _care_of_id: row.care_of_user_id,
          _care_of_name: row.care_of_user_name
        }
      })
    },
    orgOptions () {
      return [{ value: '', label: 'Any Organisation' }].concat(
        Object.keys(ORGANISATION_ID_TO_NAME).map((id) => ({ value: String(id), label: ORGANISATION_ID_TO_NAME[id] }))
      )
    },
    statusOptions () {
      const s = this.summary || {}
      const tag = (n) => (Number(n) > 0 ? ` (${n})` : '')
      return [
        { value: '',             label: 'Any Status' },
        { value: 'confident',    label: 'Confident' },
        { value: 'needs_review', label: 'Needs Review' },
        { value: 'no_match',     label: 'No Match' },
        { value: 'credited',     label: `Credited${tag(s.credited)}` },
        { value: 'rejected',     label: `Rejected${tag(s.rejected)}` },
        { value: 'oms_customer', label: `OMS Customer${tag(s.oms_customer)}` }
      ]
    },
    manualAgentOptions () {
      return [{ value: '', label: 'Use matched agent' }].concat(
        this.agents.map((u) => ({ value: String(u.id), label: u.name }))
      )
    },
    canApproveReview () {
      // Applying needs either a picked contact (fills phone + care-of) or a
      // manual agent (credits care-of only).
      return !!this.pickedContactId || !!this.manualAgentId
    },
    approveLabel () {
      if (this.pickedContactId) return 'Apply Phone + Care-of'
      if (this.manualAgentId) return 'Credit Care-of Only'
      return 'Apply'
    },
    applyAllMessage () {
      const count = Number(this.summary.confident) || 0
      return `${count} confident order${count === 1 ? '' : 's'} will get the matched customer's real phone and a care-of credit. Each order is re-checked before writing.`
    },
    rangeLabel () {
      const total = Number(this.pagination.total) || 0
      if (!total) return '0 orders'
      const page = Number(this.pagination.current_page) || 1
      const perPage = Number(this.pagination.per_page) || 25
      const start = (page - 1) * perPage + 1
      const end = Math.min(page * perPage, total)
      return `${start}–${end} of ${total}`
    }
  },
  created () {
    this.loadAgents()
    this.refresh()
  },
  methods: {
    authToken () {
      return localStorage.getItem('api_token')
    },
    parseError (error, fallback) {
      const data = error && error.response && error.response.data
      if (data) {
        if (typeof data === 'string') return data
        if (data.message) return data.message
        if (data.errors) {
          const k = Object.keys(data.errors)[0]
          if (k && Array.isArray(data.errors[k])) return data.errors[k][0]
        }
      }
      return fallback
    },
    composeAddress (o) {
      const parts = [o.contact_address, o.contact_postofficename, o.contact_district, o.contact_state, o.contact_pincode]
      return parts.filter((p) => p !== null && p !== undefined && String(p).trim() !== '').join(', ')
    },
    queryParams () {
      const p = { token: this.authToken() }
      if (this.filters.from) p.from = this.filters.from
      if (this.filters.to) p.to = this.filters.to
      if (this.filters.organisation_id) p.organisation_id = this.filters.organisation_id
      if (this.filters.status) p.status = this.filters.status
      if (this.filters.search && this.filters.search.trim()) p.search = this.filters.search.trim()
      return p
    },
    loadAgents () {
      axios.get(`${this.$apiAdress}/api/users?token=${this.authToken()}`)
        .then((response) => {
          const list = Array.isArray(response.data)
            ? response.data
            : (response && response.data && Array.isArray(response.data.users) ? response.data.users : [])
          this.agents = list.map((u) => ({ id: u.id, name: u.name || `User ${u.id}` }))
        })
        .catch(() => {})
    },
    refresh () {
      this.errorMessage = ''
      this.successMessage = ''
      this.loadSummary()
      this.loadPreview(1)
    },
    loadSummary () {
      axios.get(`${this.$apiAdress}/api/amazon-care-of/summary`, { params: this.queryParams() })
        .then((response) => {
          this.summary = { ...DEFAULT_SUMMARY, ...(response && response.data ? response.data : {}) }
        })
        .catch((error) => {
          console.log(error)
          this.summary = { ...DEFAULT_SUMMARY }
        })
    },
    loadPreview (page) {
      this.isLoading = true
      const targetPage = Number(page) || Number(this.pagination.current_page) || 1
      const params = Object.assign({}, this.queryParams(), { page: targetPage, per_page: this.pagination.per_page || 25 })

      axios.get(`${this.$apiAdress}/api/amazon-care-of/preview`, { params })
        .then((response) => {
          const data = response && response.data ? response.data : {}
          this.rows = Array.isArray(data.data) ? data.data : []
          this.pagination = {
            current_page: Number(data.current_page) || 1,
            per_page: Number(data.per_page) || 25,
            total: Number(data.total) || 0,
            last_page: Number(data.last_page) || 1
          }
        })
        .catch((error) => {
          console.log(error)
          this.rows = []
          this.errorMessage = this.parseError(error, 'Unable to load the Amazon order preview.')
        })
        .finally(() => { this.isLoading = false })
    },
    applyFilters () {
      this.refresh()
    },
    onSearchInput (value) {
      this.filters.search = value
      if (this.searchDebounce) clearTimeout(this.searchDebounce)
      this.searchDebounce = setTimeout(() => { this.refresh() }, 400)
    },
    resetFilters () {
      if (this.searchDebounce) clearTimeout(this.searchDebounce)
      this.filters = { from: '', to: todayIso(), organisation_id: '', status: '', search: '' }
      this.refresh()
    },
    goToPage (page) {
      if (Number(page) === Number(this.pagination.current_page)) return
      this.loadPreview(Number(page))
    },
    applyConfident (row) {
      const id = row._id
      if (!id || this.applyingIds[id]) return
      this.$set(this.applyingIds, id, true)
      this.errorMessage = ''
      this.successMessage = ''

      axios.post(`${this.$apiAdress}/api/amazon-care-of/${id}/apply?token=${this.authToken()}`, {
        contact_id: row._matched_contact_id,
        care_of_user_id: row._care_of_id,
        apply_phone: true
      })
        .then((response) => {
          this.successMessage = (response && response.data && response.data.message) || 'Applied.'
          this.refresh()
        })
        .catch((error) => {
          console.log(error)
          this.errorMessage = this.parseError(error, 'Apply failed.')
        })
        .finally(() => { this.$set(this.applyingIds, id, false) })
    },
    openReview (row) {
      this.reviewRow = row
      this.reviewOrderTokens = []
      this.reviewCandidates = []
      this.reviewError = ''
      this.pickedContactId = null
      this.pickedCareOfId = null
      this.manualAgentId = ''
      this.reviewOpen = true
      this.loadCandidates(row._id)
    },
    closeReview () {
      if (this.isApprovingReview) return
      this.reviewOpen = false
      this.reviewRow = null
      this.reviewCandidates = []
      this.reviewOrderTokens = []
      this.reviewError = ''
      this.pickedContactId = null
      this.pickedCareOfId = null
      this.manualAgentId = ''
    },
    loadCandidates (id) {
      if (!id) return
      this.isLoadingCandidates = true
      axios.get(`${this.$apiAdress}/api/amazon-care-of/${id}/candidates`, { params: { token: this.authToken() } })
        .then((response) => {
          const data = response && response.data ? response.data : {}
          this.reviewOrderTokens = Array.isArray(data.order_tokens) ? data.order_tokens : []
          this.reviewCandidates = Array.isArray(data.candidates) ? data.candidates : []
          // Pre-select when there's exactly one candidate — the admin just verifies.
          if (this.reviewCandidates.length === 1) {
            this.pickCandidate(this.reviewCandidates[0])
          }
        })
        .catch((error) => {
          console.log(error)
          this.reviewError = this.parseError(error, 'Unable to load candidates.')
        })
        .finally(() => { this.isLoadingCandidates = false })
    },
    pickCandidate (cand) {
      this.pickedContactId = cand.contact_id
      this.pickedCareOfId = cand.care_of_user_id || null
    },
    approveReview () {
      if (!this.reviewRow || !this.canApproveReview) return
      this.isApprovingReview = true
      this.reviewError = ''

      const payload = {}
      if (this.pickedContactId) {
        payload.contact_id = this.pickedContactId
        payload.apply_phone = true
      }
      // Manual override wins; otherwise the picked candidate's resolved agent.
      const careOf = this.manualAgentId ? Number(this.manualAgentId) : this.pickedCareOfId
      if (careOf) payload.care_of_user_id = careOf

      axios.post(`${this.$apiAdress}/api/amazon-care-of/${this.reviewRow._id}/apply?token=${this.authToken()}`, payload)
        .then((response) => {
          this.successMessage = (response && response.data && response.data.message) || 'Applied.'
          this.closeReviewAfterSuccess()
          this.refresh()
        })
        .catch((error) => {
          console.log(error)
          this.reviewError = this.parseError(error, 'Apply failed.')
        })
        .finally(() => { this.isApprovingReview = false })
    },
    closeReviewAfterSuccess () {
      this.reviewOpen = false
      this.reviewRow = null
      this.reviewCandidates = []
      this.pickedContactId = null
      this.pickedCareOfId = null
      this.manualAgentId = ''
    },
    // Dismiss a pending order from inside the review modal without crediting.
    dismissFromReview (status) {
      if (!this.reviewRow || this.isApprovingReview) return
      this.isApprovingReview = true
      this.reviewError = ''

      axios.post(`${this.$apiAdress}/api/amazon-care-of/${this.reviewRow._id}/status?token=${this.authToken()}`, { status })
        .then((response) => {
          this.successMessage = (response && response.data && response.data.message) || 'Updated.'
          this.closeReviewAfterSuccess()
          this.refresh()
        })
        .catch((error) => {
          console.log(error)
          this.reviewError = this.parseError(error, 'Update failed.')
        })
        .finally(() => { this.isApprovingReview = false })
    },
    askUncredit (row) {
      this.rowAction = {
        kind: 'uncredit',
        id: row._id,
        title: 'Reverse this credit?',
        message: 'The care-of credit will be removed, the real phone reset to the placeholder, and the order returned to the pending list so it can be re-done correctly.',
        confirmLabel: 'Uncredit',
        variant: 'danger'
      }
      this.rowActionOpen = true
    },
    askReopen (row) {
      const what = row._status === 'rejected' ? 'rejected' : 'OMS-customer'
      this.rowAction = {
        kind: 'reopen',
        id: row._id,
        title: 'Re-open to pending?',
        message: `This ${what} order will return to the pending list to be credited or dismissed again.`,
        confirmLabel: 'Re-open',
        variant: 'warning'
      }
      this.rowActionOpen = true
    },
    confirmRowAction () {
      const a = this.rowAction
      if (!a || this.rowActionBusy) return
      this.rowActionBusy = true
      this.errorMessage = ''
      this.successMessage = ''

      const url = a.kind === 'uncredit'
        ? `${this.$apiAdress}/api/amazon-care-of/${a.id}/uncredit?token=${this.authToken()}`
        : `${this.$apiAdress}/api/amazon-care-of/${a.id}/status?token=${this.authToken()}`
      const body = a.kind === 'uncredit' ? {} : { status: 'pending' }

      axios.post(url, body)
        .then((response) => {
          this.successMessage = (response && response.data && response.data.message) || 'Done.'
          this.rowActionOpen = false
          this.rowAction = null
          this.refresh()
        })
        .catch((error) => {
          console.log(error)
          this.errorMessage = this.parseError(error, 'Action failed.')
          this.rowActionOpen = false
        })
        .finally(() => { this.rowActionBusy = false })
    },
    cancelRowAction () {
      if (this.rowActionBusy) return
      this.rowActionOpen = false
      this.rowAction = null
    },
    askApplyAll () {
      if (this.isApplyingAll || !this.summary.confident) return
      this.confirmApplyAllOpen = true
    },
    confirmApplyAll () {
      this.isApplyingAll = true
      this.errorMessage = ''
      this.successMessage = ''

      axios.post(`${this.$apiAdress}/api/amazon-care-of/apply-all?token=${this.authToken()}`, this.queryParams())
        .then((response) => {
          this.successMessage = (response && response.data && response.data.message) || 'Applied.'
          this.confirmApplyAllOpen = false
          this.refresh()
        })
        .catch((error) => {
          console.log(error)
          this.errorMessage = this.parseError(error, 'Bulk apply failed.')
          this.confirmApplyAllOpen = false
        })
        .finally(() => { this.isApplyingAll = false })
    },
    cancelApplyAll () {
      if (this.isApplyingAll) return
      this.confirmApplyAllOpen = false
    },
    recompute () {
      this.isRecomputing = true
      this.errorMessage = ''
      this.successMessage = ''

      axios.post(`${this.$apiAdress}/api/amazon-care-of/recompute?token=${this.authToken()}`, this.queryParams())
        .then((response) => {
          this.summary = { ...DEFAULT_SUMMARY, ...(response && response.data ? response.data : {}) }
          this.successMessage = 'Recompute complete.'
          this.loadPreview(1)
        })
        .catch((error) => {
          console.log(error)
          this.errorMessage = this.parseError(error, 'Recompute failed.')
        })
        .finally(() => { this.isRecomputing = false })
    },
    statusLabel (status) {
      switch (status) {
        case 'confident':    return 'Confident'
        case 'needs_review': return 'Needs Review'
        case 'no_match':     return 'No Match'
        case 'credited':     return 'Credited'
        case 'rejected':     return 'Rejected'
        case 'oms_customer': return 'OMS Customer'
        default:             return status || '—'
      }
    },
    statusPillClass (status) {
      switch (status) {
        case 'confident':    return 'aco-pill--green'
        case 'needs_review': return 'aco-pill--amber'
        case 'no_match':     return 'aco-pill--gray'
        case 'credited':     return 'aco-pill--blue'
        case 'rejected':     return 'aco-pill--red'
        case 'oms_customer': return 'aco-pill--violet'
        default:             return 'aco-pill--gray'
      }
    }
  }
}
</script>

<style scoped>
.aco-page { padding: 0.25rem 0; }

.aco-toolbar { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 0.75rem; padding: 0.25rem 0.25rem 1rem; }
.aco-toolbar__title { display: flex; align-items: center; gap: 0.65rem; }
.aco-toolbar__title h1 { margin: 0; font-size: 1.15rem; font-weight: 700; color: #1f2a44; letter-spacing: -0.01em; }
.aco-toolbar__title p  { margin: 0.1rem 0 0; font-size: 0.78rem; color: #6b7280; }
.aco-toolbar__accent { width: 4px; height: 32px; border-radius: 4px; background: linear-gradient(180deg, #0e5b46 0%, #16a07a 100%); }
.aco-toolbar__actions { display: inline-flex; gap: 0.5rem; align-items: center; flex-wrap: wrap; }

.aco-recompute-btn {
  display: inline-flex; align-items: center; gap: 0.4rem;
  height: 34px; padding: 0 0.9rem; border-radius: 8px;
  background: #ffffff; border: 1px solid #e5e9f2; color: #1f2a44;
  font-weight: 600; font-size: 0.82rem; cursor: pointer;
}
.aco-recompute-btn:hover:not(:disabled) { border-color: #16a07a; color: #0e5b46; background: #f5fbf8; }
.aco-recompute-btn:disabled { opacity: 0.7; cursor: progress; }
.aco-recompute-btn svg.is-spinning { animation: aco-spin 0.9s linear infinite; }
.aco-recompute-btn:focus { outline: none; }
.aco-recompute-btn:focus-visible { outline: none; box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22); }

.aco-apply-all-btn {
  display: inline-flex; align-items: center; gap: 0.4rem;
  height: 34px; padding: 0 0.9rem; border-radius: 8px;
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  border: 0; color: #fff; font-weight: 600; font-size: 0.82rem;
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.18); cursor: pointer;
}
.aco-apply-all-btn:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 6px 14px rgba(14, 91, 70, 0.28); filter: brightness(1.05); }
.aco-apply-all-btn:disabled { opacity: 0.55; cursor: not-allowed; }
.aco-apply-all-btn:focus { outline: none; }
.aco-apply-all-btn:focus-visible { outline: none; box-shadow: 0 2px 6px rgba(14, 91, 70, 0.18), 0 0 0 3px rgba(22, 160, 122, 0.30); }
@keyframes aco-spin { to { transform: rotate(360deg); } }

.aco-alert { margin: 0 0 0.75rem; border-radius: 10px; }

.aco-tiles { display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: 0.85rem; margin-bottom: 1rem; }
.aco-tile { background: #ffffff; border: 1px solid #e5e9f2; border-radius: 12px; padding: 0.9rem 1rem; box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04); display: flex; flex-direction: column; gap: 0.25rem; min-width: 0; }
.aco-tile__label { font-size: 0.74rem; font-weight: 600; color: #6b7280; text-transform: uppercase; letter-spacing: 0.04em; }
.aco-tile__value { font-size: 1.5rem; font-weight: 700; color: #1f2a44; }
.aco-tile__breakdown { display: flex; flex-direction: column; gap: 0.1rem; font-size: 0.72rem; color: #64748b; }
.aco-tile--confident .aco-tile__value { color: #166534; }
.aco-tile--review .aco-tile__value { color: #92400e; }
.aco-tile--nomatch .aco-tile__value { color: #6b7280; }

.aco-section { background: #ffffff; border: 1px solid #e5e9f2; border-radius: 12px; box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04); margin-bottom: 1rem; }
.aco-section__head { padding: 0.7rem 1rem; border-bottom: 1px solid #eef1f7; background: #fbfcfe; border-radius: 12px 12px 0 0; }
.aco-section__head h2 { margin: 0; font-size: 0.95rem; font-weight: 700; color: #1f2a44; }
.aco-section__body { padding: 1rem; }

.aco-label { display: block; margin-bottom: 0.3rem; font-size: 0.78rem; font-weight: 600; color: #4f5560; }
.aco-filters { display: flex; flex-wrap: wrap; gap: 0.85rem; align-items: flex-end; }
.aco-field { display: flex; flex-direction: column; flex: 1 1 200px; min-width: 0; }
.aco-field--action { flex: 0 0 auto; min-width: 110px; }
.aco-field--full { flex: 1 1 100%; }

.aco-apply-btn {
  height: 36px; padding: 0 1.1rem; border-radius: 8px; border: 0;
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  color: #fff; font-weight: 600; font-size: 0.85rem; box-shadow: 0 2px 6px rgba(14, 91, 70, 0.18); cursor: pointer;
}
.aco-apply-btn:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 6px 14px rgba(14, 91, 70, 0.28); filter: brightness(1.05); }
.aco-apply-btn:disabled { opacity: 0.55; cursor: progress; }
.aco-apply-btn:focus { outline: none; }
.aco-apply-btn:focus-visible { outline: none; box-shadow: 0 2px 6px rgba(14, 91, 70, 0.18), 0 0 0 3px rgba(22, 160, 122, 0.30); }

.aco-reset-btn { height: 36px; padding: 0 1rem; border-radius: 8px; background: #ffffff; border: 1px solid #d8dde6; color: #1f2a44; font-weight: 600; font-size: 0.85rem; cursor: pointer; }
.aco-reset-btn:hover { border-color: #94a3b8; }
.aco-reset-btn:focus { outline: none; }
.aco-reset-btn:focus-visible { outline: none; box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22); }

.aco-count { font-size: 0.78rem; color: #6b7280; }

.aco-customer-cell { display: flex; flex-direction: column; gap: 0.1rem; }
.aco-customer-name { color: #1f2a44; font-weight: 500; }
.aco-customer-addr { font-size: 0.72rem; color: #64748b; max-width: 230px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

.aco-match-cell { display: flex; flex-direction: column; gap: 0.1rem; }
.aco-match-phone { color: #0e5b46; font-weight: 600; }
.aco-match-name { font-size: 0.72rem; color: #64748b; }
.aco-staff { color: #1f2a44; font-weight: 500; }
.aco-muted { color: #94a3b8; }

.aco-reason { font-size: 0.72rem; color: #64748b; margin-top: 0.2rem; max-width: 360px; }

.aco-pill { display: inline-block; padding: 0.18rem 0.55rem; border-radius: 999px; font-size: 0.72rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.02em; }
.aco-pill--green  { background: #dcfce7; color: #166534; }
.aco-pill--amber  { background: #fef3c7; color: #92400e; }
.aco-pill--gray   { background: #e5e7eb; color: #374151; }
.aco-pill--blue   { background: #dbeafe; color: #1e40af; }
.aco-pill--red    { background: #fee2e2; color: #991b1b; }
.aco-pill--violet { background: #ede9fe; color: #5b21b6; }

.aco-row-actions { display: inline-flex; gap: 0.35rem; flex-wrap: wrap; align-items: center; justify-content: center; }

.aco-act-btn {
  height: 28px; padding: 0 0.7rem; border: 0; border-radius: 6px;
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  color: #fff; font-size: 0.75rem; font-weight: 700; cursor: pointer; letter-spacing: 0.02em;
  box-shadow: 0 1px 3px rgba(14, 91, 70, 0.18);
  transition: transform 0.15s ease, box-shadow 0.15s ease, filter 0.15s ease;
}
.aco-act-btn:hover:not(:disabled) { transform: translateY(-1px); filter: brightness(1.05); box-shadow: 0 4px 10px rgba(14, 91, 70, 0.28); }
.aco-act-btn:disabled { opacity: 0.55; cursor: progress; }
.aco-act-btn:focus { outline: none; }
.aco-act-btn:focus-visible { outline: none; box-shadow: 0 1px 3px rgba(14, 91, 70, 0.18), 0 0 0 3px rgba(22, 160, 122, 0.30); }
.aco-act-btn--review { background: linear-gradient(135deg, #b45309 0%, #d97706 100%); box-shadow: 0 1px 3px rgba(180, 83, 9, 0.22); }
.aco-act-btn--review:hover:not(:disabled) { box-shadow: 0 4px 10px rgba(180, 83, 9, 0.32); }
.aco-act-btn--danger { background: linear-gradient(135deg, #b91c1c 0%, #ef4444 100%); box-shadow: 0 1px 3px rgba(185, 28, 28, 0.22); }
.aco-act-btn--danger:hover:not(:disabled) { box-shadow: 0 4px 10px rgba(185, 28, 28, 0.32); }
.aco-act-btn--danger:focus-visible { outline: none; box-shadow: 0 1px 3px rgba(185, 28, 28, 0.22), 0 0 0 3px rgba(220, 38, 38, 0.30); }
.aco-act-btn--ghost { background: #ffffff; border: 1px solid #e5e9f2; color: #1f2a44; box-shadow: none; }
.aco-act-btn--ghost:hover:not(:disabled) { border-color: #16a07a; color: #0e5b46; background: #f5fbf8; box-shadow: none; transform: none; }
.aco-act-btn--ghost:focus-visible { outline: none; box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22); }

.aco-pagination-bar { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 0.75rem; margin-top: 0.85rem; }
.aco-pagination-info { font-size: 0.78rem; color: #6b7280; }

/* Review modal */
.aco-review-body { display: flex; flex-direction: column; gap: 0.9rem; }
.aco-review-alert { background: #fee2e2; color: #991b1b; border: 1px solid #fecaca; border-radius: 8px; padding: 0.5rem 0.7rem; font-size: 0.82rem; }
.aco-review-order { background: #fbfcfe; border: 1px solid #eef1f7; border-radius: 10px; padding: 0.85rem; }
.aco-review-order__head { display: flex; flex-direction: column; gap: 0.1rem; margin-bottom: 0.5rem; }
.aco-review-eyebrow { font-size: 0.68rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; color: #d97706; }
.aco-review-order__head h3 { margin: 0; font-size: 1rem; font-weight: 700; color: #1f2a44; }
.aco-review-order__grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 0.4rem 1rem; }
.aco-review-order__addr { grid-column: 1 / -1; }
.aco-rk { display: block; font-size: 0.68rem; font-weight: 600; text-transform: uppercase; letter-spacing: 0.03em; color: #94a3b8; }
.aco-rv { font-size: 0.85rem; color: #1f2a44; }

.aco-review-head { margin-top: 0.2rem; }
.aco-review-head h4 { margin: 0; font-size: 0.9rem; font-weight: 700; color: #1f2a44; }
.aco-review-head p { margin: 0.2rem 0 0; font-size: 0.76rem; color: #6b7280; }
.aco-optional { font-weight: 500; color: #94a3b8; font-size: 0.78rem; }
.aco-review-empty { font-size: 0.82rem; color: #64748b; padding: 0.5rem 0; }

.aco-review-cards { display: flex; flex-direction: column; gap: 0.5rem; max-height: 320px; overflow-y: auto; }
.aco-cand { display: flex; gap: 0.6rem; padding: 0.65rem 0.75rem; border: 1px solid #e5e9f2; border-radius: 10px; cursor: pointer; transition: border-color 0.15s ease, background 0.15s ease; }
.aco-cand:hover { border-color: #16a07a; background: #f5fbf8; }
.aco-cand.is-picked { border-color: #16a07a; background: #f0fbf6; box-shadow: 0 0 0 1px #16a07a inset; }
.aco-cand__radio { margin-top: 0.2rem; accent-color: #0e5b46; }
.aco-cand__body { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 0.3rem; }
.aco-cand__head { display: flex; justify-content: space-between; gap: 0.5rem; }
.aco-cand__name { font-weight: 700; color: #1f2a44; }
.aco-cand__phone { color: #0e5b46; font-weight: 600; font-size: 0.85rem; }
.aco-cand__row { display: flex; flex-direction: column; gap: 0.1rem; }
.aco-cand__addr { display: block; font-size: 0.8rem; color: #475569; }

.aco-token { display: inline-block; padding: 0.08rem 0.4rem; margin: 0.1rem 0.2rem 0 0; border-radius: 999px; font-size: 0.7rem; background: #eef2f7; color: #475569; }
.aco-token--match { background: #dcfce7; color: #166534; font-weight: 600; }

.aco-manual-assign { display: flex; flex-direction: column; gap: 0.5rem; }

.aco-modal-btn { height: 34px; min-width: 90px; padding: 0.45rem 1.1rem; border-radius: 8px; font-size: 0.85rem; font-weight: 600; cursor: pointer; }
.aco-modal-btn--ghost { background: #ffffff; border: 1px solid #e5e9f2; color: #1f2a44; }
.aco-modal-btn--ghost:hover { background: #f8fafc; }
.aco-modal-btn--primary { border: 0; color: #fff; background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%); box-shadow: 0 2px 6px rgba(14, 91, 70, 0.18); }
.aco-modal-btn--primary:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 6px 14px rgba(14, 91, 70, 0.28); filter: brightness(1.05); }
.aco-modal-btn--danger { border: 0; color: #fff; background: linear-gradient(135deg, #b91c1c 0%, #ef4444 100%); box-shadow: 0 2px 6px rgba(185, 28, 28, 0.18); }
.aco-modal-btn--danger:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 6px 14px rgba(185, 28, 28, 0.28); filter: brightness(1.05); }
.aco-modal-btn--warn { border: 0; color: #fff; background: linear-gradient(135deg, #b45309 0%, #d97706 100%); box-shadow: 0 2px 6px rgba(180, 83, 9, 0.18); }
.aco-modal-btn--warn:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 6px 14px rgba(180, 83, 9, 0.28); filter: brightness(1.05); }
.aco-modal-btn:disabled { opacity: 0.6; cursor: not-allowed; }
.aco-modal-btn:focus { outline: none; }
.aco-modal-btn--ghost:focus-visible { outline: none; box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22); }
.aco-modal-btn--primary:focus-visible { outline: none; box-shadow: 0 2px 6px rgba(14, 91, 70, 0.18), 0 0 0 3px rgba(22, 160, 122, 0.30); }
.aco-modal-btn--danger:focus-visible { outline: none; box-shadow: 0 2px 6px rgba(185, 28, 28, 0.18), 0 0 0 3px rgba(220, 38, 38, 0.30); }
.aco-modal-btn--warn:focus-visible { outline: none; box-shadow: 0 2px 6px rgba(180, 83, 9, 0.18), 0 0 0 3px rgba(217, 119, 6, 0.30); }

.aco-modal-foot { display: flex; align-items: center; justify-content: space-between; gap: 0.5rem; width: 100%; flex-wrap: wrap; }
.aco-modal-foot__left, .aco-modal-foot__right { display: inline-flex; gap: 0.5rem; align-items: center; }
</style>
